import React, { useCallback, useId, useMemo, useState } from 'react'
import Radio from './Radio.component'
import type { RadioGroupProps } from './Radio.types'
import { RadioGroupContext } from './RadioContext'
import './Radio.css'

const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  defaultValue = '',
  label,
  options,
  disabled = false,
  orientation = 'vertical',
  className = '',
  darkMode = false,
  onChange,
  children,
}) => {
  const generatedName = useId()
  const [internalValue, setInternalValue] = useState(defaultValue)
  const isControlled = value !== undefined
  const currentValue = isControlled ? value : internalValue

  const handleValueChange = useCallback(
    (nextValue: string): void => {
      if (!isControlled) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [isControlled, onChange],
  )

  const contextValue = useMemo(
    () => ({
      name: name ?? generatedName,
      selectedValue: currentValue,
      disabled,
      darkMode,
      onValueChange: handleValueChange,
    }),
    [currentValue, darkMode, disabled, generatedName, handleValueChange, name],
  )

  const classNames = [
    'ds-radio-group',
    `ds-radio-group--${orientation}`,
    darkMode ? 'ds-radio-group--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <div
        className={classNames}
        role="radiogroup"
        aria-label={typeof label === 'string' ? label : undefined}
      >
        {label ? <span className="ds-radio-group__legend">{label}</span> : null}
        <div className="ds-radio-group__options">
          {options
            ? options.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                  darkMode={darkMode}
                />
              ))
            : children}
        </div>
      </div>
    </RadioGroupContext.Provider>
  )
}

export default RadioGroup
